/**
 * Knowledge Ingestion — domain models (Phase 0: the hinge).
 *
 * An Observation is an APPEND-ONLY, IMMUTABLE, evidence-bearing typed
 * mention emitted by any source (PDF extraction today; AWS / connectors
 * / manual edits in later phases). Observations are resolved against
 * the live per-folder knowledge graph and promoted — provisionally —
 * into `folder_records` with provenance.
 *
 * Immutability rule: never mutate an Observation's content. To revise,
 * write a NEW observation and set the prior row's `supersededBy`.
 *
 * See docs/architecture/knowledge-ingestion.md and
 * docs/plans/2026-05-18-feat-knowledge-ingestion-substrate-plan.md.
 */
import { z } from 'zod';
import type { RelationPredicate } from '../../../lib/graph/types';

export type * from './RepositoryWriteGate';

/** Where an observation originated. */
export type ObservationSourceKind = 'pdf' | 'aws' | 'integration' | 'manual';

/**
 * Observation lifecycle. Legal transitions (enforced in services, not
 * the DB):
 *   provisional → promoted        (resolved + promoted into folder KG)
 *   provisional → rejected        (triage rejected)
 *   provisional|promoted → superseded  (a newer observation replaces it)
 * `superseded` and `rejected` and `promoted` are terminal w.r.t. content
 * (still immutable); only `status`/`supersededBy` bookkeeping changes.
 */
export type ObservationStatus =
  | 'provisional'
  | 'promoted'
  | 'superseded'
  | 'rejected';

export const OBSERVATION_STATUSES: readonly ObservationStatus[] = [
  'provisional',
  'promoted',
  'superseded',
  'rejected',
] as const;

const ALLOWED_TRANSITIONS: Record<ObservationStatus, ObservationStatus[]> = {
  provisional: ['promoted', 'rejected', 'superseded'],
  promoted: ['superseded'],
  superseded: [],
  rejected: [],
};

export function canTransition(
  from: ObservationStatus,
  to: ObservationStatus,
): boolean {
  return ALLOWED_TRANSITIONS[from]?.includes(to) ?? false;
}

export interface Observation {
  readonly id: string;
  readonly folderId: string;
  /** Stable origin id. `jobId` for PDF extraction. */
  readonly sourceId: string;
  readonly sourceKind: ObservationSourceKind;
  /** Universal-ontology entity type (process, control, activity, …). */
  readonly entityType: string;
  readonly label: string;
  /** Verbatim evidence — REQUIRED, never synthesized. */
  readonly evidenceQuote: string;
  readonly sourceRef: Record<string, unknown>;
  readonly props: Record<string, unknown>;
  readonly confidence: number | null;
  readonly status: ObservationStatus;
  readonly supersededBy: string | null;
  readonly createdAt: string;
}

/** Validated input for creating an observation (boundary contract). */
export const ObservationInputSchema = z.object({
  id: z.string().uuid(),
  folderId: z.string().min(1),
  sourceId: z.string().min(1),
  sourceKind: z.enum(['pdf', 'aws', 'integration', 'manual']),
  entityType: z.string().min(1),
  label: z.string().min(1),
  evidenceQuote: z.string().min(1, 'verbatim evidence is required'),
  sourceRef: z.record(z.string(), z.unknown()).default({}),
  props: z.record(z.string(), z.unknown()).default({}),
  confidence: z.number().min(0).max(1).nullable().default(null),
});

export type ObservationInput = z.infer<typeof ObservationInputSchema>;

export interface RecordProvenance {
  readonly id: string;
  readonly folderRecordId: string;
  readonly observationId: string;
  readonly sourceId: string;
  readonly promotedAt: string;
}

export type PointerRelation = RelationPredicate;
export type PointerStatus = 'active' | 'dangling' | 'conflict' | 'unresolved';

export interface CrossFolderPointer {
  readonly id: string;
  readonly sourceFolderId: string;
  readonly sourceEntityId: string;
  readonly targetFolderId: string;
  readonly targetEntityId: string;
  readonly relation: PointerRelation;
  readonly decidedBy: string | null;
  readonly decisionId: string | null;
  readonly confidence: number | null;
  readonly lastSeenTargetVersion: number | null;
  readonly lastVerifiedAt: string | null;
  readonly status: PointerStatus;
}

export type ConflictClaimStatus = 'open' | 'resolved';

export interface ConflictClaim {
  readonly id: string;
  readonly folderId: string;
  readonly folderRecordId: string | null;
  readonly conflictingObservationId: string;
  readonly reason: string;
  readonly status: ConflictClaimStatus;
  readonly createdAt: string;
}

export type SourceDeltaOperation = 'add' | 'update' | 'remove' | 'snapshot';

export type SourceDeltaRetentionClass =
  | 'current_only'
  | 'rolling_window'
  | 'audit_retained'
  | 'customer_configured'
  | 'legal_hold';

export interface SourceDeltaRelationCandidate {
  readonly relation: string;
  readonly targetType?: string;
  readonly targetExternalId?: string;
  readonly targetLabel?: string;
  readonly confidence?: number;
  readonly evidence?: Record<string, unknown>;
}

export const SOURCE_INTENTS = [
  'people_directory',
  'person_hierarchy',
  'position_hierarchy',
  'org_structure',
  'project_allocation',
  'capacity_commitment',
  'team_membership',
  'workforce_demand',
  'skill_catalog',
  'skill_assertion',
  'credential_assertion',
  'skill_requirement',
  'talent_observation',
  'succession_planning',
  'task_tracker',
  'process_inventory',
  'process_raci',
  'control_risk_register',
  'access_grants',
  'system_inventory',
  'software_design_graph',
  'generic_table',
] as const;

export type SourceIntent = typeof SOURCE_INTENTS[number];

export const SOURCE_FIELD_ROLES = [
  'identity',
  'label',
  'external_ref',
  'status',
  'relationship_source',
  'relationship_target',
  'predicate',
  'allocation',
  'effort',
  'date_start',
  'date_end',
  'provenance',
  'confidence',
  'sensitivity',
  'payload',
  'unknown',
] as const;

export type SourceFieldRole = typeof SOURCE_FIELD_ROLES[number];

export const SOURCE_TOMBSTONE_POLICIES = ['tombstone', 'retain', 'review'] as const;
export type SourceTombstonePolicy = typeof SOURCE_TOMBSTONE_POLICIES[number];

export const SourceFieldMappingSchema = z.object({
  sourceField: z.string().min(1),
  targetPath: z.string().min(1),
  role: z.enum(SOURCE_FIELD_ROLES).default('unknown'),
  required: z.boolean().default(false),
  /** Optional reviewed source contract used by import schema preflight. */
  sourceType: z.enum(['string', 'number', 'boolean', 'date', 'object', 'array']).optional(),
  transform: z.string().min(1).optional(),
});

export type SourceFieldMapping =
  z.infer<typeof SourceFieldMappingSchema>;

export const SourceEntityMappingSchema = z.object({
  id: z.string().min(1),
  sourceObject: z.string().min(1).default('row'),
  ontologyType: z.string().min(1),
  profileId: z.string().min(1).optional(),
  tombstonePolicy: z.enum(SOURCE_TOMBSTONE_POLICIES).optional(),
  /** Omit an optional reference entity when every mapped source value is blank. */
  optionalWhenBlank: z.boolean().default(false),
  /** Resolve identity for relationship endpoints without emitting an entity record. */
  referenceOnly: z.boolean().default(false),
  fieldMappings: z.array(SourceFieldMappingSchema).default([]),
});

export type SourceEntityMapping =
  z.infer<typeof SourceEntityMappingSchema>;

export const SourceRelationMappingSchema = z.object({
  id: z.string().min(1),
  predicate: z.string().min(1),
  sourceEntityRef: z.string().min(1),
  targetEntityRef: z.string().min(1),
  tombstonePolicy: z.enum(SOURCE_TOMBSTONE_POLICIES).optional(),
  /**
   * Endpoint identity is required for restricted relationships whose source
   * row order is not a durable identifier (for example CSV nominations).
   */
  identity: z.enum(['source_record', 'endpoints']).default('source_record'),
  /** Skip the relation only when its optional source entity is wholly blank. */
  optionalSource: z.boolean().default(false),
  /** Skip the relation only when its optional target entity is wholly blank. */
  optionalTarget: z.boolean().default(false),
  qualifierMappings: z.array(SourceFieldMappingSchema).default([]),
  defaultQualifiers: z.record(z.string(), z.unknown()).default({}),
  confidenceField: z.string().min(1).optional(),
});

export type SourceRelationMapping =
  z.infer<typeof SourceRelationMappingSchema>;

export const SourceIdentityRuleSchema = z.object({
  id: z.string().min(1),
  entityRef: z.string().min(1),
  /** Shared namespace lets subject/manager aliases resolve to one source identity. */
  identityNamespace: z.string().min(1).optional(),
  strategy: z.enum([
    'external_ref',
    'single_field',
    'composite_fields',
    'label_match',
    'manual_review',
  ]),
  fields: z.array(z.string().min(1)).default([]),
  targetProfileId: z.string().min(1).optional(),
  confidence: z.number().min(0).max(1).optional(),
});

export type SourceIdentityRule =
  z.infer<typeof SourceIdentityRuleSchema>;

export const SourceEvidencePolicySchema = z.object({
  mode: z.enum([
    'verbatim_field',
    'row_snapshot',
    'source_identity',
    'none',
  ]),
  sourceField: z.string().min(1).optional(),
  includeFields: z.array(z.string().min(1)).default([]),
});

export type SourceEvidencePolicy =
  z.infer<typeof SourceEvidencePolicySchema>;

export const SourceMappingProfileSchema = z.object({
  id: z.string().min(1),
  /** Increment when a reviewed mapping contract changes. */
  version: z.number().int().positive().optional(),
  sourceSystem: z.string().min(1),
  intent: z.enum(SOURCE_INTENTS),
  targetFolderKind: z.string().min(1),
  targetProfileId: z.string().min(1),
  entityMappings: z.array(SourceEntityMappingSchema).default([]),
  relationMappings: z.array(SourceRelationMappingSchema).default([]),
  identityRules: z.array(SourceIdentityRuleSchema).default([]),
  evidencePolicy: SourceEvidencePolicySchema,
  retentionClass: z
    .enum([
      'current_only',
      'rolling_window',
      'audit_retained',
      'customer_configured',
      'legal_hold',
    ])
    .default('current_only'),
  requiresReview: z.boolean().default(false),
  confidence: z.number().min(0).max(1).default(0.5),
});

export type SourceMappingProfile =
  z.infer<typeof SourceMappingProfileSchema>;
export type SourceMappingProfileInput =
  z.input<typeof SourceMappingProfileSchema>;

/**
 * Stable connector delta envelope. This is the implementation contract
 * for high-volume sources before they become observations or promoted
 * folder records.
 */
export const SourceDeltaSchema = z.object({
  sourceId: z.string().min(1),
  sourceRecordId: z.string().min(1),
  sourceVersion: z.string().min(1),
  operation: z.enum(['add', 'update', 'remove', 'snapshot']),
  observedType: z.string().min(1),
  props: z.record(z.string(), z.unknown()).default({}),
  candidateRelations: z
    .array(
      z.object({
        relation: z.string().min(1),
        targetType: z.string().min(1).optional(),
        targetExternalId: z.string().min(1).optional(),
        targetLabel: z.string().min(1).optional(),
        confidence: z.number().min(0).max(1).optional(),
        evidence: z.record(z.string(), z.unknown()).optional(),
      }),
    )
    .default([]),
  confidence: z.number().min(0).max(1).nullable().default(null),
  collectedAt: z.string().datetime(),
  sourceRef: z.record(z.string(), z.unknown()).default({}),
  payloadPointer: z.string().min(1).nullable().default(null),
  idempotencyKey: z.string().min(1),
  retentionClass: z
    .enum([
      'current_only',
      'rolling_window',
      'audit_retained',
      'customer_configured',
      'legal_hold',
    ])
    .default('current_only'),
});

export type SourceDelta = z.infer<typeof SourceDeltaSchema>;

export const SourceDeltaBatchSchema = z.object({
  sourceId: z.string().min(1),
  sourceVersion: z.string().min(1),
  collectedAt: z.string().datetime(),
  deltas: z.array(SourceDeltaSchema),
});

export type SourceDeltaBatch = z.infer<typeof SourceDeltaBatchSchema>;

export interface CountBucket {
  readonly key: string;
  readonly count: number;
}

export interface GraphScaleStorageMetric {
  readonly tableName: string;
  readonly totalBytes: number;
  readonly indexBytes: number;
}

export type GraphScaleMode =
  | 'live_read'
  | 'projection_materialized'
  | 'enterprise_materialized';

export interface GraphScaleModeDecision {
  readonly mode: GraphScaleMode;
  readonly reasons: readonly string[];
  readonly thresholds: {
    readonly projectionMaterialized: {
      readonly records: number;
      readonly observations: number;
      readonly pointers: number;
      readonly projectionRows: number;
    };
    readonly enterpriseMaterialized: {
      readonly records: number;
      readonly observations: number;
      readonly pointers: number;
      readonly projectionRows: number;
    };
  };
}

export interface FolderGraphScaleSnapshot {
  readonly orgId: string;
  readonly folderId: string;
  readonly generatedAt: string;
  readonly kgVersion: number;
  readonly records: {
    readonly total: number;
    readonly withSource: number;
    readonly withoutExternalId: number;
    readonly byProfile: readonly CountBucket[];
  };
  readonly observations: {
    readonly total: number;
    readonly distinctSources: number;
    readonly byStatus: readonly CountBucket[];
    readonly bySourceKind: readonly CountBucket[];
  };
  readonly provenance: {
    readonly total: number;
  };
  readonly conflicts: {
    readonly total: number;
    readonly byStatus: readonly CountBucket[];
  };
  readonly pointers: {
    readonly outgoing: number;
    readonly incoming: number;
    readonly outgoingByStatus: readonly CountBucket[];
    readonly outgoingByRelation: readonly CountBucket[];
  };
  readonly projections: {
    readonly viewBindings: number;
    readonly processProjectionRows: number;
    readonly processTrustReports: number;
  };
  readonly storage: {
    readonly tableMetricsAvailable: boolean;
    readonly tableMetrics: readonly GraphScaleStorageMetric[];
  };
}

export interface FolderGraphScaleReport extends FolderGraphScaleSnapshot {
  readonly modeDecision: GraphScaleModeDecision;
}

export type KnowledgeGraphOutboxEventType =
  | 'folder_graph_changed'
  | 'access_policy_changed';

export type KnowledgeGraphOutboxSourceType =
  | 'promotion'
  | 'change_set'
  | 'import'
  | 'manual'
  | 'system';

export type KnowledgeGraphOutboxStatus =
  | 'pending'
  | 'processing'
  | 'processed'
  | 'failed';

export interface KnowledgeGraphOutboxEvent {
  readonly id: string;
  readonly folderId: string;
  readonly orgId: string | null;
  readonly eventType: KnowledgeGraphOutboxEventType;
  readonly graphVersion: number;
  readonly sourceType: KnowledgeGraphOutboxSourceType;
  readonly sourceId: string | null;
  readonly idempotencyKey: string;
  readonly payload: Record<string, unknown>;
  readonly status: KnowledgeGraphOutboxStatus;
  readonly attempts: number;
  readonly availableAt: string;
  readonly processedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface KnowledgeGraphOutboxProcessItemResult {
  readonly eventId: string;
  readonly folderId: string;
  readonly graphVersion: number;
  readonly status: 'processed' | 'skipped' | 'failed';
  readonly materialized?: {
    readonly kgVersion: number;
    readonly openConflicts: number;
    readonly danglingPointers: number;
    readonly inboundPointersScanned: number;
    readonly inboundPointersFlaggedDangling: number;
    readonly inboundPointersFlaggedConflict?: number;
    readonly inboundPointersRefreshedActive?: number;
    readonly memoryPriorsWritten?: number;
    readonly memoryDecisionsConsumed?: number;
    readonly memoryReplayHash?: string;
  };
  readonly error?: string;
}

export interface KnowledgeGraphOutboxProcessBatchResult {
  readonly requestedLimit: number;
  readonly claimed: number;
  readonly processed: number;
  readonly skipped: number;
  readonly failed: number;
  readonly recoveredStaleProcessing: number;
  readonly items: readonly KnowledgeGraphOutboxProcessItemResult[];
}

export interface KnowledgeGraphOutboxEmitInput {
  readonly folderId: string;
  readonly orgId?: string;
  readonly eventType: KnowledgeGraphOutboxEventType;
  readonly sourceType: KnowledgeGraphOutboxSourceType;
  readonly sourceId?: string | null;
  readonly idempotencyKey: string;
  readonly payload?: Record<string, unknown>;
}

export interface KnowledgeGraphOutboxCompareEmitInput
  extends KnowledgeGraphOutboxEmitInput {
  /** Graph version the caller reviewed. The bump and event insert share one transaction. */
  readonly expectedVersion: number;
}

export interface FolderKgVersionBumpResult {
  readonly folderId: string;
  readonly version: number;
}

export interface FolderKgVersionOutboxResult
  extends FolderKgVersionBumpResult {
  readonly event: KnowledgeGraphOutboxEvent;
  readonly reused: boolean;
}

export const KNOWN_GRAPH_RESOLUTION_DECISION_TYPES = [
  'same_as',
  'role_position',
  'external_reference',
  'owner',
  'system_link',
] as const;

export type KnownGraphResolutionDecisionType =
  typeof KNOWN_GRAPH_RESOLUTION_DECISION_TYPES[number];

export type GraphResolutionDecisionType =
  | KnownGraphResolutionDecisionType
  | RelationPredicate
  | string;

export type GraphResolutionDecisionSource =
  | 'human'
  | 'agent'
  | 'rule'
  | 'ai'
  | 'system';

export type GraphResolutionDecisionStatus = 'active' | 'reverted';
export type RepositoryDecisionSubjectKind =
  | 'record-pair'
  | 'record'
  | 'field'
  | 'source'
  | 'folder-policy'
  | 'taxonomy-binding';
export type RepositoryDecisionVerb =
  | 'confirm'
  | 'merge'
  | 'split'
  | 'rename'
  | 'reject'
  | 'override_field'
  | 'dismiss_signal'
  | 'set_policy'
  | 'supersede_source'
  | 'map_framework'
  | 'supersede_identity'
  | 'retire_endpoint'
  | GraphResolutionDecisionType;
export type RepositoryDecisionScope = 'record' | 'folder' | 'org';

export interface RepositoryDecisionAuthoritySnapshot {
  readonly basis: 'ownership_edge' | 'workspace_role' | 'delegated' | 'system' | 'unknown';
  readonly role?: string | null;
  readonly recordRef?: Record<string, unknown> | null;
  readonly snapshotAt: string;
  readonly actorKind: GraphResolutionDecisionSource;
  readonly actorId?: string | null;
  readonly notes?: string | null;
}

export interface GraphResolutionEndpoint {
  readonly kind:
    | 'folder_record'
    | 'external_record'
    | 'source_observation'
    | 'manual_concept';
  readonly key: string;
  readonly folderId?: string | null;
  readonly profileId?: string | null;
  readonly recordId?: string | null;
  readonly externalId?: string | null;
  readonly entityType?: string | null;
  readonly label?: string | null;
  readonly sourceId?: string | null;
  readonly props?: Record<string, unknown>;
}

export interface GraphResolutionDecision {
  readonly id: string;
  readonly orgId: string;
  readonly folderId: string | null;
  readonly decisionType: GraphResolutionDecisionType;
  readonly sourceKey: string;
  readonly targetKey: string;
  readonly sourceRef: GraphResolutionEndpoint;
  readonly targetRef: GraphResolutionEndpoint;
  readonly decisionSource: GraphResolutionDecisionSource;
  readonly subjectKind: RepositoryDecisionSubjectKind;
  readonly verb: RepositoryDecisionVerb | null;
  readonly scope: RepositoryDecisionScope;
  readonly authority: RepositoryDecisionAuthoritySnapshot;
  readonly validFrom: string | null;
  readonly validTo: string | null;
  readonly confidence: number | null;
  readonly evidence: Record<string, unknown>;
  readonly decidedBy: string | null;
  readonly status: GraphResolutionDecisionStatus;
  readonly idempotencyKey: string;
  readonly supersedesId: string | null;
  readonly revertedAt: string | null;
  readonly revertedBy: string | null;
  readonly revertReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface GraphResolutionDecisionInput {
  readonly orgId: string;
  readonly folderId?: string | null;
  readonly decisionType: GraphResolutionDecisionType;
  readonly sourceRef: GraphResolutionEndpoint;
  readonly targetRef: GraphResolutionEndpoint;
  readonly decisionSource: GraphResolutionDecisionSource;
  readonly subjectKind?: RepositoryDecisionSubjectKind;
  readonly verb?: RepositoryDecisionVerb | null;
  readonly scope?: RepositoryDecisionScope;
  readonly authority?: RepositoryDecisionAuthoritySnapshot | null;
  readonly validFrom?: string | Date | null;
  readonly validTo?: string | Date | null;
  readonly confidence?: number | null;
  readonly evidence?: Record<string, unknown>;
  readonly decidedBy?: string | null;
  readonly idempotencyKey?: string;
  readonly supersedesId?: string | null;
}

export type {
  CreateFolderRecordInput,
  FolderRecord,
  UpsertFolderRecordResult,
} from './FolderRecord';
